/**
 * AdminAuthObservabilityPage.jsx
 * 
 * Auth Observability Dashboard (Admin-only)
 * 
 * Features:
 * - Auth event logging (failures, successes, rate limits)
 * - IP blocklist management
 * - Force password reset
 * - Admin audit log
 * - Masked sensitive data (email, IP)
 */

import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import apiClient from "../api/client";
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  Ban,
  Clock,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Lock,
  Unlock,
  Key,
  User,
  Globe,
  Monitor,
  Copy,
  ArrowLeft,
  Filter,
} from "lucide-react";
import InlineNotice from "../components/common/InlineNotice";


// =============================================================================
// STATS CARD
// =============================================================================

function StatCard({ title, value, icon: Icon, color, subtext }) {
  const colorClasses = {
    red: "from-red-500/20 to-red-600/10 border-red-500/30 text-red-400",
    green: "from-green-500/20 to-green-600/10 border-green-500/30 text-green-400",
    amber: "from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-400",
    blue: "from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400",
  };

  return (
    <div className={`rounded-xl bg-gradient-to-br ${colorClasses[color]} border p-4`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-white/60 text-sm">{title}</span>
        <Icon className={`w-5 h-5 ${color === 'red' ? 'text-red-400' : color === 'green' ? 'text-green-400' : color === 'amber' ? 'text-amber-400' : 'text-blue-400'}`} />
      </div>
      <p className="text-2xl font-bold text-white">{value?.toLocaleString() || 0}</p>
      {subtext && <p className="text-white/40 text-xs mt-1">{subtext}</p>}
    </div>
  );
}

// =============================================================================
// ERROR CODES TABLE
// =============================================================================

function ErrorCodesTable({ data }) {
  const totalFailures = data?.reduce((sum, e) => sum + e.count, 0) || 1;

  return (
    <div className="rounded-xl bg-slate-900/50 border border-slate-800 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-800">
        <h3 className="font-medium text-white">Top Error Codes</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50">
            <tr className="text-white/50 text-left">
              <th className="px-4 py-2">Error Code</th>
              <th className="px-4 py-2">Count</th>
              <th className="px-4 py-2">%</th>
            </tr>
          </thead>
          <tbody>
            {data?.length > 0 ? data.map((error, idx) => (
              <tr key={idx} className="border-t border-slate-800/50 hover:bg-slate-800/30">
                <td className="px-4 py-2 text-red-400 font-mono text-xs">{error.error_code}</td>
                <td className="px-4 py-2 text-white">{error.count}</td>
                <td className="px-4 py-2 text-white/60">{((error.count / totalFailures) * 100).toFixed(1)}%</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="3" className="px-4 py-4 text-center text-white/40">No errors in this time range</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// TOP OFFENDERS TABLE
// =============================================================================

function TopOffendersTable({ title, data, type, onAction }) {
  const [loadingAction, setLoadingAction] = useState(null);

  const handleAction = async (item) => {
    setLoadingAction(item.ip_hash || item.email_hash);
    try {
      await onAction(item, type);
    } finally {
      setLoadingAction(null);
    }
  };

  const isIP = type === "ip";

  return (
    <div className="rounded-xl bg-slate-900/50 border border-slate-800 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-800">
        <h3 className="font-medium text-white">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50">
            <tr className="text-white/50 text-left">
              <th className="px-4 py-2">{isIP ? "IP" : "Email"}</th>
              <th className="px-4 py-2">Count</th>
              <th className="px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {data?.length > 0 ? data.map((item, idx) => (
              <tr key={idx} className="border-t border-slate-800/50 hover:bg-slate-800/30">
                <td className="px-4 py-2 text-white font-mono text-xs">
                  {isIP ? item.ip_masked : item.email_masked}
                </td>
                <td className="px-4 py-2 text-white">{item.count}</td>
                <td className="px-4 py-2">
                  <button
                    onClick={() => handleAction(item)}
                    disabled={loadingAction === (item.ip_hash || item.email_hash)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                      isIP
                        ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                        : "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
                    } disabled:opacity-50`}
                  >
                    {loadingAction === (item.ip_hash || item.email_hash) ? "..." : isIP ? "Block" : "Force Reset"}
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="3" className="px-4 py-4 text-center text-white/40">No data</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// EVENT STREAM TABLE
// =============================================================================

function EventStreamTable({ events, loading, onLoadMore, hasMore }) {
  const [expandedRow, setExpandedRow] = useState(null);

  const getEventBadge = (eventType) => {
    const badges = {
      auth_failure: { color: "red", text: "Failure" },
      auth_success: { color: "green", text: "Success" },
      auth_rate_limited: { color: "amber", text: "Rate Limited" },
      token_refresh_failure: { color: "red", text: "Refresh Fail" },
      logout_all_devices: { color: "blue", text: "Logout All" },
      verification_required: { color: "purple", text: "Verify Req" },
    };
    return badges[eventType] || { color: "gray", text: eventType };
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch {
      return isoString;
    }
  };

  return (
    <div className="rounded-xl bg-slate-900/50 border border-slate-800 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
        <h3 className="font-medium text-white">Event Stream</h3>
        <span className="text-white/40 text-xs">{events.length} events</span>
      </div>
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50 sticky top-0">
            <tr className="text-white/50 text-left">
              <th className="px-4 py-2 w-8"></th>
              <th className="px-4 py-2">Time</th>
              <th className="px-4 py-2">Event</th>
              <th className="px-4 py-2">Error</th>
              <th className="px-4 py-2">IP</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Device</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event, idx) => {
              const badge = getEventBadge(event.event_type);
              const isExpanded = expandedRow === idx;

              return (
                <React.Fragment key={event.id || idx}>
                  <tr 
                    className="border-t border-slate-800/50 hover:bg-slate-800/30 cursor-pointer"
                    onClick={() => setExpandedRow(isExpanded ? null : idx)}
                  >
                    <td className="px-4 py-2">
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-white/40" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-white/40" />
                      )}
                    </td>
                    <td className="px-4 py-2 text-white/70 text-xs whitespace-nowrap">
                      {formatTime(event.ts)}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${badge.color}-500/20 text-${badge.color}-400`}>
                        {badge.text}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-red-400 font-mono text-xs">
                      {event.error_code || "-"}
                    </td>
                    <td className="px-4 py-2 text-white/60 font-mono text-xs">
                      {event.ip_masked}
                    </td>
                    <td className="px-4 py-2 text-white/60 font-mono text-xs">
                      {event.email_masked || "-"}
                    </td>
                    <td className="px-4 py-2 text-white/60 text-xs capitalize">
                      {event.device_hint || "-"}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-slate-800/20">
                      <td colSpan="7" className="px-6 py-3">
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <span className="text-white/40">Route:</span>
                            <span className="ml-2 text-white/70 font-mono">{event.route}</span>
                          </div>
                          <div>
                            <span className="text-white/40">Status:</span>
                            <span className="ml-2 text-white/70">{event.http_status}</span>
                          </div>
                          <div>
                            <span className="text-white/40">User ID:</span>
                            <span className="ml-2 text-white/70 font-mono">{event.user_id || "-"}</span>
                          </div>
                          <div>
                            <span className="text-white/40">Lockout:</span>
                            <span className="ml-2 text-white/70">{event.lockout_seconds ? `${event.lockout_seconds}s` : "-"}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-white/40">User Agent:</span>
                            <span className="ml-2 text-white/50 text-[10px] break-all">
                              {event.user_agent_display || event.user_agent || "-"}
                            </span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-white/40">IP Hash:</span>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(event.ip_hash);
                              }}
                              className="ml-2 text-white/50 font-mono text-[10px] hover:text-white/70 flex items-center gap-1"
                            >
                              {event.ip_hash?.substring(0, 16)}...
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div className="px-4 py-3 border-t border-slate-800 text-center">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="text-pink-400 hover:text-pink-300 text-sm"
          >
            {loading ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// BLOCKLIST PANEL
// =============================================================================

function BlocklistPanel({ blocks, onUnblock, loading }) {
  return (
    <div className="rounded-xl bg-slate-900/50 border border-slate-800 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
        <h3 className="font-medium text-white flex items-center gap-2">
          <Ban className="w-4 h-4 text-red-400" />
          Active IP Blocks
        </h3>
        <span className="text-white/40 text-xs">{blocks.length} blocked</span>
      </div>
      <div className="max-h-60 overflow-y-auto">
        {blocks.length > 0 ? (
          <div className="divide-y divide-slate-800/50">
            {blocks.map((block) => (
              <div key={block.id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-white font-mono text-sm">{block.ip_masked}</p>
                  <p className="text-white/40 text-xs mt-1">{block.reason}</p>
                  {block.expires_at && (
                    <p className="text-amber-400 text-xs mt-1">
                      Expires: {new Date(block.expires_at).toLocaleString()}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => onUnblock(block)}
                  disabled={loading}
                  className="px-3 py-1 rounded-lg bg-slate-800 text-white/70 hover:bg-slate-700 text-xs"
                >
                  <Unlock className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-white/40">
            No active IP blocks
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// IP SUGGESTIONS PANEL (Auto-Suggest, Not Auto-Block)
// =============================================================================

function IPSuggestionsPanel({ suggestions, loading, onRefresh, onBlock, windowMinutes, onWindowChange, minFailures, onMinFailuresChange }) {
  const [reviewItem, setReviewItem] = useState(null);

  const getRiskBadge = (score) => {
    if (score >= 70) return { label: "High", color: "red" };
    if (score >= 40) return { label: "Med", color: "amber" };
    return { label: "Low", color: "green" };
  };

  const getFlagDescription = (flag) => {
    const descriptions = {
      high_volume: "High volume of failures",
      many_emails: "Many unique emails targeted",
      user_not_found_spike: "Possible email enumeration",
      rate_limited: "Hit rate limits",
      single_account_struggle: "May be single user struggling",
      shared_network_possible: "May be shared network (coffee shop, office)",
    };
    return descriptions[flag] || flag;
  };

  return (
    <div className="rounded-xl bg-slate-900/50 border border-slate-800 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-800">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-white flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            IP Suggestions
          </h3>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-white/50 hover:text-white/70 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="flex gap-2 text-xs">
          <select
            value={windowMinutes}
            onChange={(e) => onWindowChange(parseInt(e.target.value))}
            className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-white/70"
          >
            <option value={15}>15 min</option>
            <option value={60}>1 hour</option>
            <option value={360}>6 hours</option>
            <option value={1440}>24 hours</option>
          </select>
          <select
            value={minFailures}
            onChange={(e) => onMinFailuresChange(parseInt(e.target.value))}
            className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-white/70"
          >
            <option value={5}>5+ fails</option>
            <option value={10}>10+ fails</option>
            <option value={25}>25+ fails</option>
            <option value={50}>50+ fails</option>
          </select>
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="px-4 py-8 text-center">
            <RefreshCw className="w-6 h-6 text-pink-500 animate-spin mx-auto" />
          </div>
        ) : suggestions.length > 0 ? (
          <div className="divide-y divide-slate-800/50">
            {suggestions.map((item) => {
              const risk = getRiskBadge(item.risk_score);
              const hasSafetyFlags = item.flags.includes("single_account_struggle") || item.flags.includes("shared_network_possible");

              return (
                <div key={item.ip_hash} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-mono text-sm">{item.ip_masked}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${risk.color}-500/20 text-${risk.color}-400`}>
                        {risk.label}
                      </span>
                      {item.already_blocked && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-slate-700 text-slate-400">
                          Blocked
                        </span>
                      )}
                    </div>
                    <span className="text-white/40 text-xs">{item.failure_count} fails</span>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-white/50 mb-2">
                    <span>{item.unique_email_count} unique emails</span>
                    <span>{item.top_error_codes?.[0]?.error_code || "N/A"}</span>
                  </div>

                  {/* Safety warnings */}
                  {hasSafetyFlags && (
                    <div className="mb-2 px-2 py-1 rounded bg-amber-500/10 border border-amber-500/20">
                      <p className="text-amber-300 text-xs flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {item.flags.includes("single_account_struggle")
                          ? "May be single user struggling - consider password reset instead"
                          : "May be shared network - blocking could affect legitimate users"}
                      </p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => setReviewItem(item)}
                      className="px-3 py-1 rounded bg-slate-800 text-white/70 hover:bg-slate-700 text-xs"
                    >
                      Review
                    </button>
                    {!item.already_blocked && (
                      <button
                        onClick={() => onBlock(item)}
                        className="px-3 py-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 text-xs"
                      >
                        Block
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-white/40">
            No suspicious IPs detected
          </div>
        )}
      </div>

      {/* Review Modal */}
      {reviewItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Review IP: {reviewItem.ip_masked}
            </h2>

            <div className="space-y-4 mb-6">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-slate-800">
                  <p className="text-white/50 text-xs">Failures</p>
                  <p className="text-white text-lg font-bold">{reviewItem.failure_count}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800">
                  <p className="text-white/50 text-xs">Unique Emails</p>
                  <p className="text-white text-lg font-bold">{reviewItem.unique_email_count}</p>
                </div>
              </div>

              {/* Risk Score */}
              <div className="p-3 rounded-lg bg-slate-800">
                <p className="text-white/50 text-xs mb-1">Risk Score</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        reviewItem.risk_score >= 70 ? "bg-red-500" :
                        reviewItem.risk_score >= 40 ? "bg-amber-500" : "bg-green-500"
                      }`}
                      style={{ width: `${reviewItem.risk_score}%` }}
                    />
                  </div>
                  <span className="text-white font-bold">{reviewItem.risk_score}</span>
                </div>
              </div>

              {/* Error Codes */}
              <div className="p-3 rounded-lg bg-slate-800">
                <p className="text-white/50 text-xs mb-2">Error Code Breakdown</p>
                <div className="space-y-1">
                  {reviewItem.top_error_codes?.map((ec) => (
                    <div key={ec.error_code} className="flex items-center justify-between text-sm">
                      <span className="text-red-400 font-mono text-xs">{ec.error_code}</span>
                      <span className="text-white">{ec.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Flags */}
              {reviewItem.flags?.length > 0 && (
                <div className="p-3 rounded-lg bg-slate-800">
                  <p className="text-white/50 text-xs mb-2">Detected Patterns</p>
                  <div className="flex flex-wrap gap-2">
                    {reviewItem.flags.map((flag) => (
                      <span
                        key={flag}
                        className={`px-2 py-1 rounded text-xs ${
                          flag.includes("single") || flag.includes("shared")
                            ? "bg-amber-500/20 text-amber-400"
                            : "bg-red-500/20 text-red-400"
                        }`}
                        title={getFlagDescription(flag)}
                      >
                        {getFlagDescription(flag)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Time Range */}
              <div className="text-xs text-white/40">
                First seen: {reviewItem.first_seen ? new Date(reviewItem.first_seen).toLocaleString() : "N/A"}
                <br />
                Last seen: {reviewItem.last_seen ? new Date(reviewItem.last_seen).toLocaleString() : "N/A"}
              </div>
            </div>

            {/* Warning */}
            <div className="mb-4 px-3 py-2 rounded bg-amber-500/10 border border-amber-500/20">
              <p className="text-amber-300 text-xs">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Blocking shared networks can affect legitimate users.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setReviewItem(null)}
                className="flex-1 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700"
              >
                Cancel
              </button>
              {!reviewItem.already_blocked && (
                <>
                  <button
                    onClick={() => {
                      onBlock(reviewItem, reviewItem.recommended_duration_hours || 24);
                      setReviewItem(null);
                    }}
                    className="flex-1 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600"
                  >
                    Block {reviewItem.recommended_duration_hours || 24}h
                  </button>
                  <button
                    onClick={() => {
                      onBlock(reviewItem, null);
                      setReviewItem(null);
                    }}
                    className="py-2 px-4 rounded-lg bg-red-500 text-white hover:bg-red-600"
                  >
                    Permanent
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MODALS
// =============================================================================

function BlockIPModal({ isOpen, onClose, onSubmit, loading }) {
  const [ip, setIp] = useState("");
  const [reason, setReason] = useState("");
  const [duration, setDuration] = useState("permanent");

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ip,
      reason,
      expires_in_hours: duration === "permanent" ? null : parseInt(duration),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Ban className="w-5 h-5 text-red-400" />
          Block IP Address
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white/70 text-sm mb-1">IP Address</label>
            <input
              type="text"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              placeholder="e.g., 192.168.1.1"
              className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-pink-500 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-1">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Brute force attack"
              className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-pink-500 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-1">Duration</label>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-pink-500 outline-none"
            >
              <option value="permanent">Permanent</option>
              <option value="1">1 hour</option>
              <option value="24">24 hours</option>
              <option value="168">1 week</option>
              <option value="720">30 days</option>
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
            >
              {loading ? "Blocking..." : "Block IP"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ForceResetModal({ isOpen, onClose, onSubmit, loading, email }) {
  const [reason, setReason] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ email, reason });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-amber-400" />
          Force Password Reset
        </h2>
        <p className="text-white/60 text-sm mb-4">
          This will require the user to reset their password on next login.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white/70 text-sm mb-1">Email (masked)</label>
            <input
              type="text"
              value={email || ""}
              disabled
              className="w-full px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-white/50"
            />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-1">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Suspicious activity detected"
              className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-pink-500 outline-none"
              required
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
            >
              {loading ? "Processing..." : "Force Reset"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function AdminAuthObservabilityPage() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  // State
  const [timeRange, setTimeRange] = useState("24h");
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [notice, setNotice] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    errorCode: "",
    eventType: "",
    email: "",
    ip: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  // Modals
  const [blockIPModal, setBlockIPModal] = useState(false);
  const [forceResetModal, setForceResetModal] = useState({ open: false, email: null });
  const [modalLoading, setModalLoading] = useState(false);

  // IP Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionWindowMinutes, setSuggestionWindowMinutes] = useState(60);
  const [suggestionMinFailures, setSuggestionMinFailures] = useState(10);

  // Check admin access
  useEffect(() => {
    if (user && user.role !== "admin" && !user.is_admin) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await apiClient.get(`/admin/auth-observability/summary`, {
        params: { range: timeRange },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(res.data);
    } catch (err) {
      console.error("[AuthObservability] Failed to fetch summary:", err);
      if (err.response?.status === 403) {
        navigate("/dashboard");
      }
    }
  }, [token, timeRange, navigate]);

  // Fetch events
  const fetchEvents = useCallback(async (append = false) => {
    if (!token) return;
    
    setEventsLoading(true);
    try {
      const params = {
        limit: 50,
        ...(append && nextCursor ? { cursor: nextCursor } : {}),
        ...(filters.errorCode ? { error_code: filters.errorCode } : {}),
        ...(filters.eventType ? { event_type: filters.eventType } : {}),
        ...(filters.email ? { email: filters.email } : {}),
        ...(filters.ip ? { ip: filters.ip } : {}),
      };

      const res = await apiClient.get(`/admin/auth-observability/events`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (append) {
        setEvents((prev) => [...prev, ...res.data.events]);
      } else {
        setEvents(res.data.events);
      }
      setNextCursor(res.data.next_cursor);
    } catch (err) {
      console.error("[AuthObservability] Failed to fetch events:", err);
    } finally {
      setEventsLoading(false);
    }
  }, [token, nextCursor, filters]);

  // Fetch blocklist
  const fetchBlocklist = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await apiClient.get(`/admin/auth-observability/blocklist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlocks(res.data.blocks);
    } catch (err) {
      console.error("[AuthObservability] Failed to fetch blocklist:", err);
    }
  }, [token]);

  // Fetch IP suggestions
  const fetchSuggestions = useCallback(async () => {
    if (!token) return;
    
    setSuggestionsLoading(true);
    try {
      const res = await apiClient.get(`/admin/auth-observability/ip-suggestions`, {
        params: {
          window_minutes: suggestionWindowMinutes,
          min_failures: suggestionMinFailures,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuggestions(res.data.suggestions || []);
    } catch (err) {
      console.error("[AuthObservability] Failed to fetch suggestions:", err);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [token, suggestionWindowMinutes, suggestionMinFailures]);

  // Initial fetch
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchEvents(), fetchBlocklist(), fetchSuggestions()]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchSummary, fetchEvents, fetchBlocklist, fetchSuggestions]);

  // Re-fetch events when filters change
  useEffect(() => {
    fetchEvents(false);
  }, [filters.errorCode, filters.eventType]); // eslint-disable-line

  // Re-fetch suggestions when window/threshold changes
  useEffect(() => {
    fetchSuggestions();
  }, [suggestionWindowMinutes, suggestionMinFailures]); // eslint-disable-line

  // Handlers
  const handleBlockIP = async (data) => {
    setModalLoading(true);
    try {
      await apiClient.post(`/admin/auth-observability/block-ip`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotice({ type: "success", message: "IP blocked successfully" });
      setBlockIPModal(false);
      fetchBlocklist();
      fetchSummary();
      fetchSuggestions();
    } catch (err) {
      setNotice({ type: "error", message: err.response?.data?.detail || "Failed to block IP" });
    } finally {
      setModalLoading(false);
    }
  };

  // Block IP from suggestion (using hash)
  const handleBlockFromSuggestion = async (suggestion, durationHours) => {
    try {
      await apiClient.post(`/admin/auth-observability/block-ip-hash`, {
        ip_hash: suggestion.ip_hash,
        ip_masked: suggestion.ip_masked,
        reason: `Blocked via auto-suggest: ${suggestion.failure_count} failures, ${suggestion.unique_email_count} unique emails`,
        expires_in_hours: durationHours,
        evidence: {
          failure_count: suggestion.failure_count,
          unique_email_count: suggestion.unique_email_count,
          top_error_codes: suggestion.top_error_codes,
          risk_score: suggestion.risk_score,
          flags: suggestion.flags,
        },
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotice({ type: "success", message: `IP ${suggestion.ip_masked} blocked successfully` });
      fetchBlocklist();
      fetchSuggestions();
    } catch (err) {
      setNotice({ type: "error", message: err.response?.data?.detail || "Failed to block IP" });
    }
  };

  const handleUnblockIP = async (block) => {
    try {
      await apiClient.post(`/admin/auth-observability/unblock-ip`, {
        ip_hash: block.ip_hash,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotice({ type: "success", message: "IP unblocked successfully" });
      fetchBlocklist();
      fetchSuggestions();
    } catch (err) {
      setNotice({ type: "error", message: err.response?.data?.detail || "Failed to unblock IP" });
    }
  };

  const handleForceReset = async (data) => {
    setModalLoading(true);
    try {
      await apiClient.post(`/admin/auth-observability/force-password-reset`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotice({ type: "success", message: "Password reset required for user" });
      setForceResetModal({ open: false, email: null });
    } catch (err) {
      setNotice({ type: "error", message: err.response?.data?.detail || "Failed to force password reset" });
    } finally {
      setModalLoading(false);
    }
  };

  const handleOffenderAction = async (item, type) => {
    if (type === "ip") {
      // For IP, we need the raw IP which we don't have
      // Open modal instead
      setBlockIPModal(true);
    } else {
      // For email
      setForceResetModal({ open: true, email: item.email_masked });
    }
  };

  const handleRefresh = () => {
    fetchSummary();
    fetchEvents(false);
    fetchBlocklist();
  };

  const handleApplyFilters = () => {
    fetchEvents(false);
    setShowFilters(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-pink-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black">
      {/* Header */}
      <header className="px-4 py-4 border-b border-slate-800 bg-black/80 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin")}
              className="text-white/50 hover:text-white/70 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-pink-500" />
                Auth Observability
              </h1>
              <p className="text-white/50 text-xs">Security monitoring dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Time Range Selector */}
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
              data-testid="time-range-selector"
            >
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            <button
              onClick={handleRefresh}
              className="px-3 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Notice */}
        {notice && (
          <InlineNotice
            type={notice.type}
            message={notice.message}
            onDismiss={() => setNotice(null)}
          />
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Auth Failures"
            value={summary?.totals?.auth_failure}
            icon={AlertTriangle}
            color="red"
          />
          <StatCard
            title="Successes"
            value={summary?.totals?.auth_success}
            icon={CheckCircle}
            color="green"
          />
          <StatCard
            title="Rate Limited"
            value={summary?.totals?.auth_rate_limited}
            icon={Clock}
            color="amber"
          />
          <StatCard
            title="Refresh Failures"
            value={summary?.totals?.token_refresh_failure}
            icon={RefreshCw}
            color="blue"
          />
        </div>

        {/* Top Row: Error Codes + Offenders */}
        <div className="grid md:grid-cols-3 gap-6">
          <ErrorCodesTable data={summary?.top_error_codes} />
          <TopOffendersTable
            title="Top IPs (Failures)"
            data={summary?.top_ips}
            type="ip"
            onAction={handleOffenderAction}
          />
          <TopOffendersTable
            title="Top Emails (Failures)"
            data={summary?.top_email_hashes}
            type="email"
            onAction={handleOffenderAction}
          />
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm"
          >
            <Filter className="w-4 h-4" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
          <button
            onClick={() => setBlockIPModal(true)}
            className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 text-sm flex items-center gap-2"
          >
            <Ban className="w-4 h-4" />
            Block IP
          </button>
        </div>

        {showFilters && (
          <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800 flex flex-wrap gap-4">
            <div>
              <label className="block text-white/50 text-xs mb-1">Error Code</label>
              <select
                value={filters.errorCode}
                onChange={(e) => setFilters({ ...filters, errorCode: e.target.value })}
                className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
              >
                <option value="">All</option>
                <option value="USER_NOT_FOUND">USER_NOT_FOUND</option>
                <option value="WRONG_PASSWORD">WRONG_PASSWORD</option>
                <option value="TOO_MANY_ATTEMPTS">TOO_MANY_ATTEMPTS</option>
                <option value="BANNED">BANNED</option>
                <option value="TOKEN_EXPIRED">TOKEN_EXPIRED</option>
              </select>
            </div>
            <div>
              <label className="block text-white/50 text-xs mb-1">Event Type</label>
              <select
                value={filters.eventType}
                onChange={(e) => setFilters({ ...filters, eventType: e.target.value })}
                className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
              >
                <option value="">All</option>
                <option value="auth_failure">Failure</option>
                <option value="auth_success">Success</option>
                <option value="auth_rate_limited">Rate Limited</option>
              </select>
            </div>
            <div>
              <label className="block text-white/50 text-xs mb-1">Search Email</label>
              <input
                type="text"
                value={filters.email}
                onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                placeholder="Full email to search"
                className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm w-48"
              />
            </div>
            <div>
              <label className="block text-white/50 text-xs mb-1">Search IP</label>
              <input
                type="text"
                value={filters.ip}
                onChange={(e) => setFilters({ ...filters, ip: e.target.value })}
                placeholder="Full IP to search"
                className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm w-40"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 rounded-lg bg-pink-500 text-white hover:bg-pink-600 text-sm"
              >
                Apply
              </button>
            </div>
          </div>
        )}

        {/* Bottom Row: Events + Blocklist + Suggestions */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <EventStreamTable
              events={events}
              loading={eventsLoading}
              onLoadMore={() => fetchEvents(true)}
              hasMore={!!nextCursor}
            />
          </div>
          <div className="space-y-6">
            <IPSuggestionsPanel
              suggestions={suggestions}
              loading={suggestionsLoading}
              onRefresh={fetchSuggestions}
              onBlock={handleBlockFromSuggestion}
              windowMinutes={suggestionWindowMinutes}
              onWindowChange={setSuggestionWindowMinutes}
              minFailures={suggestionMinFailures}
              onMinFailuresChange={setSuggestionMinFailures}
            />
            <BlocklistPanel
              blocks={blocks}
              onUnblock={handleUnblockIP}
              loading={false}
            />
          </div>
        </div>
      </main>

      {/* Modals */}
      <BlockIPModal
        isOpen={blockIPModal}
        onClose={() => setBlockIPModal(false)}
        onSubmit={handleBlockIP}
        loading={modalLoading}
      />
      <ForceResetModal
        isOpen={forceResetModal.open}
        onClose={() => setForceResetModal({ open: false, email: null })}
        onSubmit={handleForceReset}
        loading={modalLoading}
        email={forceResetModal.email}
      />
    </div>
  );
}
